import { AllianceApplicationsIndicator } from '@/components/AllianceApplications';
import { AllianceHelpFab } from '@/components/AllianceHelp';
import { BootLoader } from '@/components/BootLoader';
import { AttackIndicator } from '@/components/IncomingAttacks';
// Игровой layout: панель ресурсов сверху, нижняя навигация (5 табов), свайпы между экранами,
// меню разделов (Drawer), индикатор соединения, фейерверк при смене правящего, safe area.
import { NotificationBell } from '@/components/NotificationBell';
import { PushOffer } from '@/components/PushOffer';
import { TutorialGuide } from '@/components/TutorialGuide';
import { prefetchGameBackgrounds } from '@/lib/backgrounds';
import { preloadGround } from '@/map/ground3d';
import { haptic } from '@/lib/device';
import { shortWords } from '@/lib/format';
import { autoFullscreenOnFirstTap, fullscreenSupported, toggleFullscreen } from '@/lib/fullscreen';
import { loadMarches, notifyGameChanged, onGameChanged, reloadCastle } from '@/lib/hooks';
import { syncPush } from '@/lib/pwa';
import { raceCastleBg } from '@/lib/raceArt';
import { useHasCity3D } from '@/town/City3D';
import { isMuted, onMuteChange, toggleMute } from '@/lib/sound';
import { projectResources, useGame } from '@/store/game';
import { CloseButton, Countdown, Drawer, Icon, IconButton, ResIcon, cx, useNow } from '@/ui';
import { RESOURCE_TYPES } from '@sk/shared';
import type { UiIcon } from '@sk/shared/icons';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

const TABS: { path: string; icon: UiIcon; key: string }[] = [
  { path: '/game/map', icon: 'map', key: 'map' },
  { path: '/game/fields', icon: 'fields', key: 'fields' },
  { path: '/game', icon: 'city', key: 'city' },
  { path: '/game/army', icon: 'army', key: 'army' },
  { path: '/game/sea', icon: 'sea', key: 'sea' },
  { path: '/game/alliance', icon: 'alliance', key: 'alliance' },
  { path: '/game/shop', icon: 'shop', key: 'shop' },
];

/** «Места», из которых открываются окна: замок, поля ресурсов и карта мира. */
const BASE_PATHS = ['/game', '/game/fields', '/game/map'];

const MENU: { path: string; icon: UiIcon; key: string }[] = [
  { path: '/game/quests', icon: 'quests', key: 'quests' },
  { path: '/game/boosts', icon: 'gift', key: 'boosts' },
  { path: '/game/battle-pass', icon: 'trophy', key: 'battlePass' },
  { path: '/game/marches', icon: 'clock', key: 'marches' },
  { path: '/game/reports', icon: 'report', key: 'reports' },
  { path: '/game/heroes', icon: 'hero', key: 'heroes' },
  { path: '/game/sea', icon: 'sea', key: 'sea' },
  { path: '/game/politics', icon: 'trophy', key: 'politics' },
  { path: '/game/events', icon: 'gift', key: 'events' },
  { path: '/game/rankings', icon: 'trophy', key: 'rankings' },
  { path: '/game/battle-math', icon: 'army', key: 'battleMath' },
  { path: '/game/mail', icon: 'mail', key: 'mail' },
  { path: '/game/profile', icon: 'settings', key: 'profile' },
];

function ResourceBar() {
  const now = useNow(1000);
  const state = useGame();
  const res = projectResources(state, now);
  const navigate = useNavigate();
  if (!res) return <div className="h-[48px]" />;
  // Сетка 3×2: все пять ресурсов и золото видны целиком даже на экране 360 px
  const cell =
    'sk-res h-[19px] whitespace-nowrap text-[12px] font-bold tracking-tight text-parchment-100 sm:text-[13px]';
  return (
    <div className="grid max-w-[620px] grid-cols-3 gap-x-1 gap-y-1 py-1 pl-1.5 pr-1 leading-none tabular-nums">
      {RESOURCE_TYPES.map((r) => (
        <span key={r} className={cell} title={r}>
          <ResIcon type={r} size={17} />
          <span className="min-w-0 truncate">{shortWords(res[r])}</span>
        </span>
      ))}
      <button type="button" onClick={() => navigate('/game/shop')} className={cell}>
        <ResIcon type="gems" size={17} />
        <span className="min-w-0 truncate">{shortWords(state.gems)}</span>
      </button>
    </div>
  );
}

const MARCH_ICON: Record<string, string> = {
  attack: '⚔️',
  capture: '🏴',
  scout: '🔭',
  reinforce: '🛡️',
  gather: '⛏️',
};

/** Полоса таймеров под шапкой: атаки на город (красные) и свои походы с отсчётом до цели. */
function MarchTimers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const incoming = useGame((s) => s.incoming);
  const marches = useGame((s) => s.marches);
  const reload = () => void loadMarches().catch(() => undefined);
  const attacks = [...incoming].sort((a, b) => a.arriveAt.localeCompare(b.arriveAt));
  const own = [...marches]
    .filter(
      (m) => m.status === 'moving' || m.status === 'returning' || (m.status === 'stationed' && m.gather),
    )
    .sort((a, b) => a.arriveAt.localeCompare(b.arriveAt));
  if (attacks.length === 0 && own.length === 0) return null;
  const chip = 'flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold tabular-nums';
  return (
    <div className="flex gap-1.5 overflow-x-auto px-2 pb-1.5" aria-label={t('game:march.list')}>
      {attacks.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => navigate('/game/mail?tab=attacks')}
          className={cx(chip, 'animate-pulse bg-blood-500 text-white')}
        >
          ⚠️ {t('game:march.underAttack')} {a.toX}:{a.toY} · <Countdown to={a.arriveAt} onDone={reload} />
        </button>
      ))}
      {own.slice(0, 4).map((m) => (
        <button
          key={m.id}
          type="button"
          onClick={() => navigate('/game/marches')}
          className={cx(chip, 'border border-gold-500/70 bg-ink-800 text-gold-200')}
          aria-label={`${t(`game:march.${m.type}`)} ${m.toX}:${m.toY}`}
        >
          {m.status === 'returning' ? '↩️' : (MARCH_ICON[m.type] ?? '⚔️')} {m.toX}:{m.toY} ·{' '}
          <Countdown to={m.arriveAt} onDone={reload} />
        </button>
      ))}
      {own.length > 4 && (
        <button type="button" onClick={() => navigate('/game/marches')} className={cx(chip, 'bg-ink-800')}>
          +{own.length - 4}
        </button>
      )}
    </div>
  );
}

export function GameLayout(): ReactNode {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [muted, setMuted] = useState(isMuted());
  useEffect(() => onMuteChange(setMuted), []);
  useEffect(() => {
    void syncPush().catch(() => undefined);
  }, []);
  const connected = useGame((s) => s.connected);
  const incoming = useGame((s) => s.incoming);
  const header = useRef<HTMLElement>(null);
  // Откуда открыт текущий экран: крестик возвращает в замок, на поля или на карту (с её позицией)
  const origin = useRef('/game');
  const isBase = BASE_PATHS.includes(location.pathname);
  useEffect(() => {
    if (isBase) origin.current = location.pathname + location.search;
  }, [isBase, location.pathname, location.search]);

  // Реальная высота шапки (с полосой таймеров) — для полноэкранных страниц (замок, поля, карта, чат)
  useEffect(() => {
    const el = header.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--sk-header', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Полный экран (без адресной строки браузера) — с первого касания в игре
  useEffect(() => autoFullscreenOnFirstTap(), []);
  // Фоны замка и полей — в кэш сразу после входа, чтобы экраны открывались без задержки
  const castleBg = raceCastleBg(useGame((s) => s.user?.race));
  useEffect(() => prefetchGameBackgrounds(castleBg), [castleBg]);
  // Карта мира: земля, высоты, деревья, мосты — в память сразу при входе в игру (карта открывается без ожидания)
  useEffect(() => preloadGround(), []);

  // Живое обновление: любое изменение на сервере (стройка, найм, поход вернулся) → свежие замок,
  // армия и походы без перезагрузки страницы. Возврат во вкладку тоже перечитывает состояние.
  useEffect(() => {
    void reloadCastle().catch(() => undefined);
    void loadMarches().catch(() => undefined);
    const off = onGameChanged(() => {
      void reloadCastle().catch(() => undefined);
      void loadMarches().catch(() => undefined);
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') notifyGameChanged();
    };
    document.addEventListener('visibilitychange', onVisible);
    const attackPoll = window.setInterval(() => {
      if (!document.hidden) void loadMarches().catch(() => undefined);
    }, 10_000);
    return () => {
      off();
      window.clearInterval(attackPoll);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Страховка, если событие сокета потерялось: обновиться сразу после ближайшего окончания
  // стройки или прибытия похода.
  const castle = useGame((s) => s.castle);
  // 3D-город расы: поля ресурсов — часть сцены города, вкладка «Поля» скрыта
  const city3d = useHasCity3D(castle?.castle.race);
  const tabs = city3d ? TABS.filter((tab) => tab.key !== 'fields') : TABS;
  const marches = useGame((s) => s.marches);
  useEffect(() => {
    const times = [
      ...(castle?.buildings ?? []).map((b) => b.upgradeFinishAt),
      ...marches.filter((m) => m.status === 'moving' || m.status === 'returning').map((m) => m.arriveAt),
    ]
      .filter((v): v is string => !!v)
      .map((v) => new Date(v).getTime())
      .filter((v) => v > Date.now() - 5_000);
    if (times.length === 0) return;
    const delay = Math.max(1_000, Math.min(...times) - Date.now() + 1_500);
    if (delay > 24 * 3_600_000) return;
    const timer = window.setTimeout(notifyGameChanged, delay);
    return () => window.clearTimeout(timer);
  }, [castle, marches]);

  return (
    <div className="flex min-h-full flex-col bg-ink-900">
      <header
        ref={header}
        className="sk-panel sticky top-0 z-30 rounded-b-xl !border-t-0 pt-[calc(env(safe-area-inset-top)+var(--sk-top-extra))]"
      >
        <div className="flex items-center">
          <div className="min-w-0 flex-1">
            <ResourceBar />
          </div>
          {/* Звук и меню — за золотым разделителем, как на макете */}
          <div className="ml-auto flex items-center self-stretch border-l border-gold-500/60 py-1 pl-0.5 pr-0.5">
            <NotificationBell />
            <AttackIndicator />
            <AllianceApplicationsIndicator />
            <IconButton
              icon={muted ? 'mute' : 'sound'}
              label={t(muted ? 'game:profile.soundOn' : 'game:profile.soundOff')}
              onClick={() => setMuted(toggleMute())}
              iconSize={22}
              className="!h-10 !w-10 text-parchment-100"
            />
            <span aria-hidden className="h-5 w-px bg-gold-500/40" />
            <IconButton
              icon="settings"
              label={t('more')}
              tut="nav-more"
              onClick={() => setMenu(true)}
              iconSize={22}
              className="!h-10 !w-10 text-parchment-100"
            />
          </div>
        </div>
        {!connected && (
          <div role="status" className="bg-blood-500/80 px-3 py-1 text-center text-sm">
            {t('reconnecting')}
          </div>
        )}
        <MarchTimers />
      </header>

      <main className="flex-1 pb-[calc(72px+env(safe-area-inset-bottom))]">
        {!isBase && (
          <div className="pointer-events-none sticky top-[var(--sk-header,49px)] z-20 flex justify-end px-2 pt-2">
            <CloseButton className="pointer-events-auto shadow-lg" onClose={() => navigate(origin.current)} />
          </div>
        )}
        <Outlet />
      </main>
      <AllianceHelpFab />
      <TutorialGuide />
      <BootLoader />
      <PushOffer />

      <nav
        aria-label="main"
        // На iPhone с иконки рабочего стола iOS может недодать высоту — меню опускается до края экрана
        className="sk-panel fixed inset-x-0 bottom-[calc(-1*var(--sk-ios-gap))] z-30 rounded-t-xl !border-b-0 px-1 pb-[calc(4px+env(safe-area-inset-bottom))] pt-1"
      >
        <ul className={cx('mx-auto grid max-w-xl', city3d ? 'grid-cols-6' : 'grid-cols-7')}>
          {tabs.map((tab, i) => (
            <li
              key={tab.path}
              className={cx(
                'relative',
                // Тонкие золотые разделители между вкладками
                i > 0 &&
                  'before:absolute before:inset-y-3 before:left-0 before:w-px before:bg-gradient-to-b before:from-transparent before:via-gold-500/60 before:to-transparent',
              )}
            >
              <NavLink
                to={tab.path}
                data-tut={`nav-${tab.key}`}
                end={tab.path === '/game'}
                onClick={() => haptic(6)}
                className={({ isActive }) =>
                  cx(
                    'relative z-[1] flex h-[62px] flex-col items-center justify-center gap-1 rounded-lg text-[11px] font-semibold tracking-tight sm:text-[14px] sm:tracking-normal',
                    isActive
                      ? 'sk-nav-active border border-gold-400 text-gold-300 shadow-[inset_0_0_10px_rgba(255,206,110,.35),0_0_8px_rgba(224,180,76,.35)]'
                      : 'text-parchment-100',
                  )
                }
              >
                <Icon name={tab.icon} size={26} />
                {t(`nav.${tab.key}`)}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <Drawer open={menu} onClose={() => setMenu(false)} title={t('appName')}>
        <ul className="grid gap-1">
          {fullscreenSupported() && (
            <li>
              <button
                type="button"
                onClick={() => {
                  setMenu(false);
                  void toggleFullscreen();
                }}
                className="flex min-h-[48px] w-full items-center gap-3 rounded-xl px-3 text-left text-[16px] hover:bg-ink-700"
              >
                <span className="w-6 text-center text-xl">⛶</span>
                {t('fullscreen')}
              </button>
            </li>
          )}
          {MENU.map((m) => (
            <li key={m.path}>
              <NavLink
                to={m.path}
                data-tut={`menu-${m.key}`}
                onClick={() => setMenu(false)}
                className={({ isActive }) =>
                  cx(
                    'flex min-h-[48px] items-center gap-3 rounded-xl px-3 text-[16px]',
                    isActive ? 'bg-gold-500 text-night' : 'hover:bg-ink-700',
                  )
                }
              >
                <Icon name={m.icon} size={24} />
                {t(`menu.${m.key}`)}
              </NavLink>
            </li>
          ))}
        </ul>
      </Drawer>
    </div>
  );
}
